import { useQueries, useQuery } from "@tanstack/react-query";
import {
  Archive,
  ArrowUpRight,
  BookOpenText,
  Bot,
  CircleHelp,
  FolderKanban,
  Gauge,
  LibraryBig,
  Menu,
  PanelRightOpen,
  Plus,
  Search,
  Settings,
  Sparkles,
  WandSparkles,
  X,
} from "lucide-react";
import { useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { api, type KnowledgeBuild } from "@/api/client";
import { Button, Logo, StatusBadge } from "@/components/primitives";
import { formatDate, humanKnowledgeStatus, knowledgeStatusTone } from "@/lib/research";

type AppShellProps = {
  projectIds: string[];
  onStartProject: () => void;
};

const navigation = [
  { to: "/", label: "工作台", icon: Gauge, end: true },
  { to: "/projects", label: "研究项目", icon: FolderKanban },
  { to: "/library", label: "内容库", icon: LibraryBig },
  { to: "/deliveries", label: "交付中心", icon: Archive },
];

const tools = [
  { to: "/tools/transcripts", label: "批量逐字稿", icon: WandSparkles },
  { to: "/tools/profiles", label: "主页链接采集", icon: BookOpenText },
];

function ProjectTaskCard({ project, onGo }: { project: KnowledgeBuild; onGo: () => void }) {
  return (
    <button className="task-row" onClick={onGo} type="button">
      <span className="task-art task-art-small"><Sparkles size={16} /></span>
      <span className="task-row-main">
        <strong>{project.request.knowledge_base_name}</strong>
        <small>{project.message}</small>
      </span>
      <span className="task-row-meta">
        <StatusBadge tone={knowledgeStatusTone(project.status)}>{humanKnowledgeStatus(project.status)}</StatusBadge>
        <small>{project.progress}%</small>
      </span>
    </button>
  );
}

function TaskDrawer({ ids, open, onClose }: { ids: string[]; open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const history = useQuery({
    queryKey: ["knowledge", "history"],
    queryFn: api.listKnowledge,
    retry: false,
    staleTime: 2_000,
    refetchInterval: 3_000,
  });
  const allIds = [...new Set([...ids, ...(history.data?.map((project) => project.id) || [])])];
  const queries = useQueries({
    queries: allIds.slice(0, 12).map((id) => ({
      queryKey: ["knowledge", id],
      queryFn: () => api.getKnowledge(id),
      staleTime: 1_000,
      refetchInterval: 2_500,
      retry: false,
    })),
  });
  const projects = queries
    .map((query) => query.data)
    .filter((item): item is KnowledgeBuild => Boolean(item))
    .sort((a, b) => b.updated_at.localeCompare(a.updated_at));
  const needingAction = projects.filter((project) =>
    ["waiting_user", "partial", "failed", "cancelled"].includes(project.status),
  );
  const running = projects.filter((project) =>
    ["queued", "collecting", "transcribing", "building", "packaging"].includes(project.status),
  );
  const complete = projects.filter((project) => project.status === "succeeded");

  const go = (id: string) => {
    navigate(`/projects/${id}`);
    onClose();
  };

  return (
    <>
      {open && <button aria-label="关闭任务中心" className="drawer-backdrop" onClick={onClose} type="button" />}
      <aside className={`task-drawer ${open ? "is-open" : ""}`} aria-label="任务中心" aria-hidden={!open}>
        <header className="drawer-header">
          <div>
            <p className="eyebrow">TASK CENTER</p>
            <h2>任务中心</h2>
          </div>
          <Button aria-label="关闭任务中心" tone="quiet" className="icon-button" onClick={onClose}>
            <X size={18} />
          </Button>
        </header>
        <p className="drawer-copy">任务中心从服务端项目列表读取真实状态，自动同步正在运行和需要人工介入的任务。</p>
        {allIds.length === 0 ? (
          <div className="drawer-empty">还没有可追踪的研究任务。</div>
        ) : (
          <div className="drawer-groups">
            <TaskGroup title="需要你处理" items={needingAction} onGo={go} empty="暂无需要人工介入的任务" />
            <TaskGroup title="正在运行" items={running} onGo={go} empty="当前没有运行中的任务" />
            <TaskGroup title="已完成" items={complete} onGo={go} empty="暂无完成的交付" />
          </div>
        )}
      </aside>
    </>
  );
}

function TaskGroup({
  title,
  items,
  onGo,
  empty,
}: {
  title: string;
  items: KnowledgeBuild[];
  onGo: (id: string) => void;
  empty: string;
}) {
  return (
    <section className="task-group">
      <div className="task-group-title"><span>{title}</span><small>{items.length}</small></div>
      {items.length ? (
        items.map((project) => <ProjectTaskCard key={project.id} project={project} onGo={() => onGo(project.id)} />)
      ) : (
        <p className="task-group-empty">{empty}</p>
      )}
    </section>
  );
}

export function AppShell({ projectIds, onStartProject }: AppShellProps) {
  const [taskDrawerOpen, setTaskDrawerOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const projectHistory = useQuery({
    queryKey: ["knowledge", "history"],
    queryFn: api.listKnowledge,
    retry: false,
    staleTime: 2_000,
    refetchInterval: 3_000,
  });
  const taskCount = projectHistory.data?.length ?? projectIds.length;
  const location = useLocation();
  const toolActive = location.pathname.startsWith("/tools") || location.pathname === "/profiles";

  return (
    <div className="app-shell">
      <aside className={`sidebar ${mobileMenuOpen ? "is-open" : ""}`}>
        <div className="sidebar-top">
          <NavLink className="sidebar-brand" to="/" onClick={() => setMobileMenuOpen(false)}>
            <Logo />
          </NavLink>
          <Button className="sidebar-new" onClick={onStartProject} icon={<Plus size={16} />}>
            新建研究
          </Button>
        </div>

        <nav className="sidebar-nav" aria-label="主导航">
          <p>工作空间</p>
          {navigation.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
              end={end}
              key={to}
              to={to}
              onClick={() => setMobileMenuOpen(false)}
            >
              <Icon size={18} />
              <span>{label}</span>
            </NavLink>
          ))}
          <div className={`nav-tools ${toolActive ? "has-active" : ""}`}>
            <p>快捷工具</p>
            {tools.map(({ to, label, icon: Icon }) => (
              <NavLink
                className={({ isActive }) => `nav-item nav-item-sub ${isActive ? "active" : ""}`}
                key={to}
                to={to}
                onClick={() => setMobileMenuOpen(false)}
              >
                <Icon size={17} />
                <span>{label}</span>
              </NavLink>
            ))}
          </div>
        </nav>

        <div className="sidebar-bottom">
          <NavLink className="nav-item" to="/help" onClick={() => setMobileMenuOpen(false)}>
            <CircleHelp size={18} /><span>帮助与设置</span>
          </NavLink>
          <div className="sidebar-account">
            <span className="avatar">研</span>
            <span><strong>本地工作台</strong><small>单机模式</small></span>
            <Settings size={16} />
          </div>
        </div>
      </aside>

      <main className="main-stage">
        <header className="topbar">
          <Button
            className="icon-button menu-toggle"
            tone="quiet"
            aria-label="打开导航"
            onClick={() => setMobileMenuOpen((value) => !value)}
          >
            <Menu size={19} />
          </Button>
          <div className="search-field" aria-label="全局搜索暂未开放">
            <Search size={17} />
            <span>搜索项目、博主或内容</span>
            <kbd>⌘ K</kbd>
          </div>
          <div className="topbar-actions">
            <button className="task-center-button" onClick={() => setTaskDrawerOpen(true)} type="button">
              <span className="task-center-icon"><Bot size={18} /></span>
              <span>任务中心</span>
              {taskCount > 0 && <b>{taskCount}</b>}
            </button>
            <button className="topbar-avatar" type="button" aria-label="本地工作台账户">研</button>
          </div>
        </header>
        <div className="page-stage"><Outlet /></div>
      </main>
      {mobileMenuOpen && <button aria-label="关闭导航" className="mobile-menu-backdrop" onClick={() => setMobileMenuOpen(false)} type="button" />}
      <TaskDrawer ids={projectIds} open={taskDrawerOpen} onClose={() => setTaskDrawerOpen(false)} />
    </div>
  );
}
